<template>
  <State
    v-if="
      (item.assets_count === 0 || (assets.length === 0 && !isLoading)) &&
      isEmpty(detail)
    "
    :type="state.type"
    :title="state.title"
    :text="state.text"
    class="self-center grow"
  />
  <template v-else>
    <PassportAsset
      v-for="asset in assets"
      :key="asset.id"
      :asset="asset"
      :canUpdate="canUpdate"
      @click="handleShowDetail(asset)"
      @unlink="handleUnlink(asset)"
      class="cursor-pointer"
    />
    <template v-if="isLoading">
      <PassportAssetSkeleton v-for="i of [...Array(10).keys()]" :key="i" />
    </template>
    <span
      ref="loadMoreTrigger"
      v-show="isLoading || assets.length"
      class="w-full h-px block"
    />
    <Show
      v-if="detail"
      :withBackdropButton="withBackdropButton"
      :asset="detail"
      @close="detail = null"
    />
  </template>
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { isEmpty } from "lodash";
import { useIntersectionObserver } from "@vueuse/core";
import { onMounted, onUnmounted, ref, computed, inject } from "vue";
import { storeToRefs } from "pinia";
import {
  PassportAsset,
  PassportAssetSkeleton,
} from "@/SharedComponents/Passport";
import { State, useState } from "@/SharedComponents/State";
import { useFilters } from "@/Composables/useFilters";
import { toast } from "@/SharedComponents/Toaster";
import { useI18n } from "vue-i18n";
import { default as Show } from "@/Pages/Assets/Detail.vue";

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Refs
const assets = ref([]);
const page = ref(null);
const isLoading = ref(false);
const loadMoreTrigger = ref(null);
const isIntersectingLoadMoreTrigger = ref(false);
const per_page = ref(25);
const detail = ref(null);

// Define Props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  withBackdropButton: { type: Boolean, default: false },
  filter: { type: Object, required: true },
  canUpdate: {
    type: Boolean,
    required: true,
  },
});

// Emits
const emit = defineEmits(["update:item"]);

// Emitter
const emitter = inject("emitter");

// Translations
const { t } = useI18n();

// State
const stateStore = useState("asset");
const { state } = storeToRefs(stateStore);

// Computed props
const fullyLoaded = computed(() => {
  return (
    page.value?.meta &&
    page.value?.meta.last_page === page.value?.meta.current_page
  );
});

// Functions
const { stop } = useIntersectionObserver(
  loadMoreTrigger,
  ([{ isIntersecting }]) => {
    isIntersectingLoadMoreTrigger.value = isIntersecting;
    // Don't trigger multiple times while running
    if (isIntersecting && !isLoading.value) {
      getAssets();
    }
  }
);

async function getAssets() {
  if (fullyLoaded.value) {
    return;
  }

  isLoading.value = true;

  const parameters = {
    page: page.value?.meta.current_page + 1,
    tenant: globalFilterValues.value.tenant,
    tenant_only: false,
    per_page: per_page.value,
    ...props.filter,
  };

  const url = route("api.assets.search");

  await axios
    .post(url, {
      ...parameters,
    })
    .then((response) => {
      page.value = response.data;
      assets.value = [...assets.value, ...response.data.data];

      setTimeout(() => {
        if (isIntersectingLoadMoreTrigger.value) {
          getAssets();
        }
      }, 100);
    })
    .catch((err) => {
      console.log("error occurred", err);
    })
    .finally(() => {
      isLoading.value = false;
    });
}

const handleUnlink = (asset) => {
  const url =
    "asset_groups" in props.filter
      ? "api.assets.unlink.group"
      : "api.assets.unlink.space";

  axios
    .delete(route(url, asset))
    .then(() => {
      toast.success(t("toasts.assets.unlink.success"));
      assets.value = assets.value.filter((a) => a.id !== asset.id);

      emit("update:item", {
        ...props.item,
      });
    })
    .catch((error) => {
      if (error.response.status === 403) {
        toast.error(t("toasts.files.unlink.403"));
        return;
      }

      toast.error(
        error.response.data.message || t("toasts.files.unlink.error")
      );
    });
};

const handleShowDetail = (asset) => {
  axios.get(route("api.assets.show", [asset])).then((response) => {
    detail.value = response.data.data;
  });
};

emitter.on("update-asset-list-data", (e) => {
  assets.value = assets.value.map((asset) => {
    const updatedAsset = e.data.find((a) => a.id === asset.id);
    return updatedAsset ? updatedAsset : asset;
  });

  assets.value = assets.value.filter(
    (asset) =>
      asset.asset_group?.id === props.item.id ||
      asset.space?.id === props.item.id
  );

  emit("update:item", {
    ...props.item,
  });
});

emitter.on("remove-asset-list-data", (e) => {
  assets.value = assets.value.filter((asset) => !e.data.includes(asset.id));

  emit("update:item", {
    ...props.item,
  });
});

// Lifecycle Hooks
onMounted(() => {
  getAssets();
});

onUnmounted(() => {
  stop();
});
</script>
